import {
    Box, Button, Typography, Sheet, Checkbox, Chip,
    FormControl, FormLabel, Slider, Select, Option,
    Input, Stack, Divider, CircularProgress, Card, Tabs, TabList, Tab, TabPanel
} from "@mui/joy";
import { useContext, useState, useEffect } from "react";
import { LayoutContext } from "../context";
import { APIGetContentTree, APICreateTest, APICreateClinicalTest, detectTrialLocked } from "../api";
import LimitReachedModal from "../components/modals/LimitReachedModal";
import { useNavigate } from "react-router";
import { Play, CaretDown, CaretRight, GraduationCap, Exam, Hash, PencilSimple, Stethoscope } from "@phosphor-icons/react";


export default function TestPage() {
    const { token, user, loadUserTests, openUpgradeModal } = useContext(LayoutContext);
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [questionStats, setQuestionStats] = useState(null);
    const [limitReached, setLimitReached] = useState(false);
    const [limitMessage, setLimitMessage] = useState('');
    const [limitVariant, setLimitVariant] = useState('paywall');
    const [formData, setFormData] = useState({
        title: '',
        mode: 'study',
        question_selection_mode: 'new',
        total_questions: 20,
        category_ids: []
    });

    // Загрузка категорий при монтировании компонента
    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setLoadingCategories(true);
        try {
            const data = await APIGetContentTree(token);
            if (data.success) {
                setCategories(data.categories);
                if (data.stats) {
                    setQuestionStats(data.stats);
                }
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoadingCategories(false);
        }
    };

    // Функция для получения всех ID дочерних категорий (рекурсивно)
    const getAllChildCategoryIds = (category) => {
        let childIds = [];
        if (category.children && category.children.length > 0) {
            category.children.forEach(child => {
                childIds.push(child.id);
                childIds = [...childIds, ...getAllChildCategoryIds(child)];
            });
        }
        return childIds;
    };

    // Функция для получения всех ID категорий в поддереве
    const getAllCategoryIdsInSubtree = (category) => {
        return [category.id, ...getAllChildCategoryIds(category)];
    };

    // Проверка, выбраны ли все дочерние категории
    const areAllChildrenSelected = (category) => {
        const allChildIds = getAllChildCategoryIds(category);
        return allChildIds.length > 0 && allChildIds.every(id => selectedCategories.includes(id));
    };

    // Проверка, выбрана ли хотя бы одна дочерняя категория
    const isAnyChildSelected = (category) => {
        const allChildIds = getAllChildCategoryIds(category);
        return allChildIds.some(id => selectedCategories.includes(id));
    };

    // Переключение раскрытия категории
    const toggleExpand = (categoryId) => {
        setExpandedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    // Обработчик переключения категории
    const handleCategoryToggle = (category) => {
        const allCategoryIdsInSubtree = getAllCategoryIdsInSubtree(category);
        const isCurrentlySelected = selectedCategories.includes(category.id);

        setSelectedCategories(prev => {
            let newSelection;
            if (isCurrentlySelected) {
                newSelection = prev.filter(id => !allCategoryIdsInSubtree.includes(id));
            } else {
                newSelection = [...new Set([...prev, ...allCategoryIdsInSubtree])];
            }
            setFormData(prevFormData => ({
                ...prevFormData,
                category_ids: newSelection
            }));
            return newSelection;
        });
    };

    // Вычисление доступных вопросов в зависимости от выбранного типа
    const getAvailableQuestionsCount = () => {
        if (!questionStats) return null;

        switch (formData.question_selection_mode) {
            case 'new':
                return questionStats.remaining || 0;
            case 'incorrect':
                return questionStats.incorrect_count || 0;
            case 'favourite':
                return questionStats.favourite_count || 0;
            case 'mixed':
            default:
                return questionStats.total_questions || 0;
        }
    };

    // Получение названия для плитки в зависимости от типа вопросов
    const getAvailableQuestionsLabel = () => {
        switch (formData.question_selection_mode) {
            case 'new':
                return 'Новых';
            case 'incorrect':
                return 'С ошибками';
            case 'favourite':
                return 'Избранных';
            case 'mixed':
            default:
                return 'Доступно';
        }
    };

    // Подсчёт вопросов в выбранных категориях (только листовые, чтобы избежать двойного подсчёта)
    const getSelectedCategoriesQuestionCount = () => {
        if (selectedCategories.length === 0) return 0;

        const countQuestionsInCategory = (category) => {
            const isSelected = selectedCategories.includes(category.id);
            const hasChildren = category.children && category.children.length > 0;

            if (hasChildren) {
                return category.children.reduce((sum, child) => sum + countQuestionsInCategory(child), 0);
            } else {
                return isSelected ? getCategoryDisplayCount(category) : 0;
            }
        };

        return categories.reduce((sum, category) => sum + countQuestionsInCategory(category), 0);
    };

    const handleGenerateTest = async () => {
        if (selectedCategories.length === 0) {
            alert('Выберите хотя бы одну категорию');
            return;
        }

        setLoading(true);
        try {
            let data;
            if (activeTab === 1) {
                // Клинические задачи
                data = await APICreateClinicalTest({
                    category_ids: formData.category_ids,
                    num_questions: formData.total_questions,
                }, token);
            } else {
                // Мини-тесты (обычные вопросы без клинических случаев)
                data = await APICreateTest(formData, token);
            }

            if (data.success) {
                navigate(`/test-session/${data.test.id}`);
                loadUserTests();
            } else {
                alert('Ошибка при генерации теста: ' + (data.error || 'Неизвестная ошибка'));
            }
        } catch (error) {
            // Trial-locked (категория вне триального пула) — показываем upgrade modal
            const trialLock = detectTrialLocked(error);
            if (trialLock && openUpgradeModal) {
                openUpgradeModal(trialLock.message);
                return;
            }
            // Paywall vs quota:
            //  - paywall:true → настоящий paywall, показываем плашку с тарифами
            //  - daily_limit_reached:true БЕЗ paywall → юзер с админским бустом упёрся
            //    в свой кастомный лимит, плашка без тарифов («приходи завтра»)
            const data = error.response?.data;
            const isPaywall = data?.paywall === true;
            const isQuota = data?.daily_limit_reached === true && !isPaywall;
            if (isPaywall || isQuota) {
                setLimitMessage(data.error || '');
                setLimitVariant(isPaywall ? 'paywall' : 'quota');
                setLimitReached(true);
                return;
            }
            console.error('Error creating test:', error);
            alert('Ошибка при создании теста: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    // Получить ВСЕ ID категорий из дерева (рекурсивно)
    const getAllCategoryIds = (cats) => {
        let ids = [];
        for (const cat of cats) {
            ids.push(cat.id);
            if (cat.children && cat.children.length > 0) {
                ids = [...ids, ...getAllCategoryIds(cat.children)];
            }
        }
        return ids;
    };

    const isAdmin = user?.role === 'admin';
    const regularCategories = categories.filter(c => c.name !== 'IM Residency');

    const visibleCategories = regularCategories;

    const allCategoryIds = visibleCategories.length > 0 ? getAllCategoryIds(visibleCategories) : [];
    const isAllSelected = allCategoryIds.length > 0 && allCategoryIds.every(id => selectedCategories.includes(id));

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedCategories([]);
            setFormData(prev => ({ ...prev, category_ids: [] }));
        } else {
            setSelectedCategories(allCategoryIds);
            setFormData(prev => ({ ...prev, category_ids: allCategoryIds }));
        }
    };

    const handleTabChange = (_, value) => {
        setActiveTab(value);
        setSelectedCategories([]);
        setFormData(prev => ({ ...prev, category_ids: [] }));
    };

    // Получение количества вопросов по категории в зависимости от фильтра и таба
    const getCategoryDisplayCount = (category) => {
        // Таб "Клинические задачи" — всегда показываем clinical_count
        if (activeTab === 1) {
            return category.clinical_count ?? 0;
        }
        // Таб "Мини-тесты" — фильтр по типу
        const mode = formData.question_selection_mode;
        switch (mode) {
            case 'new':
                return category.new_count ?? (category.question_count - (category.solved_count || 0));
            case 'incorrect':
                return category.incorrect_count ?? 0;
            case 'favourite':
                return category.favourite_count ?? 0;
            case 'mixed':
            default:
                return category.question_count;
        }
    };

    // Цвет чипа в зависимости от фильтра
    const getCategoryChipColor = (category) => {
        const mode = formData.question_selection_mode;
        const count = getCategoryDisplayCount(category);
        if (count === 0) return "neutral";
        switch (mode) {
            case 'new': return "primary";
            case 'incorrect': return "danger";
            case 'favourite': return "warning";
            case 'mixed':
            default:
                return category.solved_count > 0 ? "success" : "neutral";
        }
    };

    // Компонент категории с раскрывающимся списком
    const CategoryItem = ({ category, level = 0 }) => {
        const hasChildren = category.children && category.children.length > 0;
        const isSelected = selectedCategories.includes(category.id);
        const isExpanded = expandedCategories.includes(category.id);
        const allChildrenSelected = hasChildren && areAllChildrenSelected(category);
        const someChildrenSelected = hasChildren && isAnyChildSelected(category) && !allChildrenSelected;
        const displayCount = getCategoryDisplayCount(category);
        const chipColor = getCategoryChipColor(category);

        return (
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.75,
                        px: 1,
                        ml: level * 2,
                        borderRadius: 'sm',
                        '&:hover': {
                            bgcolor: 'background.level1'
                        }
                    }}
                >
                    {/* Кнопка раскрытия */}
                    {hasChildren ? (
                        <Box
                            onClick={() => toggleExpand(category.id)}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                mr: 0.5,
                                color: 'text.secondary',
                                '&:hover': { color: 'text.primary' }
                            }}
                        >
                            {isExpanded ? (
                                <CaretDown size={18} />
                            ) : (
                                <CaretRight size={18} />
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ width: 24, mr: 0.5 }} />
                    )}

                    <Checkbox
                        checked={isSelected}
                        indeterminate={someChildrenSelected}
                        onChange={() => handleCategoryToggle(category)}
                        color="primary"
                        sx={{ mr: 1 }}
                    />

                    <Typography
                        level="body-sm"
                        sx={{
                            fontWeight: hasChildren ? 600 : 400,
                            flex: 1,
                            cursor: hasChildren ? 'pointer' : 'default',
                            color: hasChildren ? 'text.primary' : 'text.secondary'
                        }}
                        onClick={() => hasChildren && toggleExpand(category.id)}
                    >
                        {category.name}
                    </Typography>

                    <Chip size="sm" variant="soft" color={chipColor}>
                        {formData.question_selection_mode === 'mixed'
                            ? `${category.solved_count || 0}/${category.question_count}`
                            : displayCount
                        }
                    </Chip>
                </Box>

                {/* Дочерние категории */}
                {hasChildren && isExpanded && (
                    <Box>
                        {category.children.map(child => (
                            <CategoryItem
                                key={child.id}
                                category={child}
                                level={level + 1}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
        <Box sx={{ display: 'flex' }}>
            <Box
                component="main"
                className="TestContent"
                sx={{
                    px: { xs: 2, md: 7 },
                    pt: 5,
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Создать тест
                    </Typography>
                </Box>

                {/* Статистика вопросов — Apple style */}
                {questionStats && (
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                        gap: 1.5,
                    }}>
                        {(() => {
                            const acc = questionStats.accuracy || 0;
                            const totalForTab = activeTab === 0
                                ? (questionStats.regular_questions_count || 0)
                                : (questionStats.clinical_cases_count || 0);
                            return [
                                { label: activeTab === 0 ? 'Мини-тестов' : 'Клин. задач', value: totalForTab },
                                {
                                    label: 'Решено',
                                    value: questionStats.unique_answered,
                                    suffix: ` (${questionStats.total_questions > 0 ? Math.round((questionStats.unique_answered / questionStats.total_questions) * 100) : 0}%)`,
                                },
                                { label: getAvailableQuestionsLabel(), value: getAvailableQuestionsCount() },
                                { label: 'Точность', value: `${acc}%`, accent: true },
                            ].map((stat, i) => (
                                <Box key={i} sx={{
                                    p: 2.5, borderRadius: '14px',
                                    bgcolor: (theme) => stat.accent
                                        ? (theme.palette.mode === 'dark' ? 'rgba(120,140,93,0.15)' : 'rgba(120,140,93,0.12)')
                                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F5F4F1'),
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : '#EEEDEA',
                                    },
                                }}>
                                    <Typography sx={{
                                        fontSize: '0.78rem', fontWeight: 500, letterSpacing: '-0.005em',
                                        color: stat.accent ? ((theme) => theme.palette.mode === 'dark' ? '#9AAF7B' : '#788C5D') : 'text.tertiary',
                                        mb: 0.5,
                                    }}>{stat.label}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                        <Typography sx={{
                                            fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.03em',
                                            color: stat.accent ? ((theme) => theme.palette.mode === 'dark' ? '#9AAF7B' : '#788C5D') : 'text.primary',
                                            lineHeight: 1,
                                        }}>{stat.value}</Typography>
                                        {stat.suffix && (
                                            <Typography sx={{
                                                fontSize: '0.8rem', fontWeight: 450, color: 'text.tertiary',
                                                letterSpacing: '-0.01em',
                                            }}>{stat.suffix}</Typography>
                                        )}
                                    </Box>
                                </Box>
                            ));
                        })()}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* Левая колонка - Категории */}
                    <Sheet
                        variant="outlined"
                        sx={{
                            flex: 2,
                            p: 2,
                            borderRadius: 'lg',
                            maxHeight: 'calc(100vh - 200px)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2, '--Tabs-gap': '4px' }}>
                            <TabList size="sm" variant="soft" sx={{ borderRadius: 'lg', p: 0.5 }}>
                                <Tab value={0} sx={{ borderRadius: 'md', fontWeight: 500, gap: 0.75 }}>
                                    <Exam size={14} />
                                    Мини-тесты
                                </Tab>
                                <Tab value={1} sx={{ borderRadius: 'md', fontWeight: 500, gap: 0.75 }}>
                                    <Stethoscope size={14} />
                                    Клинические задачи
                                </Tab>
                            </TabList>
                        </Tabs>
                        <Typography level="body-xs" sx={{ mb: 1.5, color: 'text.tertiary' }}>
                            {activeTab === 0
                                ? 'Закрепление фактов из First Aid. Быстрые вопросы по теории.'
                                : 'Полноценная проработка материала. Клинические виньетки в стиле UWorld.'}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography level="title-md">
                                Выберите категории
                            </Typography>
                            <Checkbox
                                checked={isAllSelected}
                                indeterminate={selectedCategories.length > 0 && !isAllSelected}
                                onChange={handleSelectAll}
                                label="ВСЕ ВОПРОСЫ"
                                size="sm"
                                sx={{
                                    fontWeight: 600,
                                    '& .MuiCheckbox-label': {
                                        fontSize: '0.8rem',
                                    }
                                }}
                            />
                        </Box>

                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            pr: 1
                        }}>
                            {loadingCategories ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : visibleCategories.length > 0 ? (
                                visibleCategories.map(category => (
                                    <CategoryItem key={category.id} category={category} />
                                ))
                            ) : (
                                <Typography color="neutral" sx={{ textAlign: 'center', py: 4 }}>
                                    Нет доступных категорий
                                </Typography>
                            )}
                        </Box>

                        {selectedCategories.length > 0 && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography level="body-sm" color="primary">
                                    Выбрано категорий: {selectedCategories.length}
                                </Typography>
                            </Box>
                        )}
                    </Sheet>

                    {/* Правая колонка - Настройки теста */}
                    <Sheet
                        variant="outlined"
                        sx={{
                            flex: 1,
                            p: 3,
                            borderRadius: 'lg',
                            alignSelf: 'flex-start'
                        }}
                    >
                        <Typography level="title-md" sx={{ mb: 3 }}>
                            Настройки теста
                        </Typography>

                        <Stack spacing={2}>
                            {/* Название теста */}
                            <Card variant="soft" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                    <PencilSimple size={20} />
                                    <Typography level="title-sm" fontWeight={600}>
                                        Название теста
                                    </Typography>
                                </Stack>
                                <Input
                                    placeholder="Введите название"
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        title: e.target.value
                                    }))}
                                    sx={{ bgcolor: 'background.surface' }}
                                />
                            </Card>

                            {/* Режим тестирования */}
                            <Card variant="soft" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                    <GraduationCap size={20} />
                                    <Typography level="title-sm" fontWeight={600}>
                                        Режим тестирования
                                    </Typography>
                                </Stack>
                                <Select
                                    value={formData.mode}
                                    onChange={(e, value) => setFormData(prev => ({
                                        ...prev,
                                        mode: value
                                    }))}
                                    sx={{ bgcolor: 'background.surface' }}
                                >
                                    <Option value="study">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip size="sm" color="success" variant="soft">Учебный</Chip>
                                            <Typography level="body-xs" color="neutral">— с подсказками</Typography>
                                        </Stack>
                                    </Option>
                                    <Option value="exam">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip size="sm" color="danger" variant="soft">Экзамен</Chip>
                                            <Typography level="body-xs" color="neutral">— без подсказок</Typography>
                                        </Stack>
                                    </Option>
                                </Select>
                            </Card>

                            {/* Тип вопросов */}
                            <Card variant="soft" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                    <Exam size={20} />
                                    <Typography level="title-sm" fontWeight={600}>
                                        Тип вопросов
                                    </Typography>
                                    {getAvailableQuestionsCount() !== null && (
                                        <Chip size="sm" color="primary" variant="soft">
                                            {getAvailableQuestionsCount()} доступно
                                        </Chip>
                                    )}
                                </Stack>
                                <Select
                                    value={formData.question_selection_mode}
                                    onChange={(e, value) => setFormData(prev => ({
                                        ...prev,
                                        question_selection_mode: value
                                    }))}
                                    sx={{ bgcolor: 'background.surface' }}
                                >
                                    <Option value="new">Только новые</Option>
                                    <Option value="incorrect">С ошибками</Option>
                                    <Option value="favourite">Избранные</Option>
                                    <Option value="mixed">Все вопросы</Option>
                                </Select>
                            </Card>

                            {/* Количество вопросов */}
                            <Card variant="soft" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Hash size={20} />
                                    <Typography level="title-sm" fontWeight={600}>
                                        Количество вопросов
                                    </Typography>
                                    <Chip size="sm" color="primary" variant="solid">
                                        {formData.total_questions}
                                    </Chip>
                                </Stack>
                                {selectedCategories.length > 0 && (
                                    <Typography level="body-xs" sx={{ mb: 1.5, color: 'text.secondary' }}>
                                        Доступно в выбранных категориях: <b>{getSelectedCategoriesQuestionCount()}</b>
                                    </Typography>
                                )}
                                <Slider
                                    value={formData.total_questions}
                                    min={10}
                                    max={250}
                                    step={1}
                                    onChange={(e, value) => setFormData(prev => ({
                                        ...prev,
                                        total_questions: value
                                    }))}
                                    valueLabelDisplay="auto"
                                />
                                <Input
                                    type="number"
                                    size="sm"
                                    value={formData.total_questions}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value) || 1;
                                        setFormData(prev => ({
                                            ...prev,
                                            total_questions: Math.max(1, Math.min(250, value))
                                        }));
                                    }}
                                    slotProps={{
                                        input: { min: 1, max: 60 }
                                    }}
                                    sx={{ mt: 1, bgcolor: 'background.surface' }}
                                />
                            </Card>

                            <Divider sx={{ my: 1 }} />

                            <Button
                                size="lg"
                                startDecorator={<Play size={18} />}
                                onClick={handleGenerateTest}
                                loading={loading}
                                disabled={selectedCategories.length === 0}
                                fullWidth
                                sx={{
                                    py: 1.5,
                                    fontSize: 'md',
                                    fontWeight: 600
                                }}
                            >
                                Начать тест
                            </Button>
                        </Stack>
                    </Sheet>
                </Box>
            </Box>
        </Box>
        <LimitReachedModal
            open={limitReached}
            onClose={() => setLimitReached(false)}
            message={limitMessage}
            token={token}
            variant={limitVariant}
        />
        </>
    );
}
